import React from 'react';
import { pdf } from '@react-pdf/renderer';
import { Label } from '@/components/ui/label';
import { FileText, Upload, CheckCircle2, Eye } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import ContractPDF, { ContractData } from '@/pdf/templates/ContractPDF';
import BillPDF, { BillData } from '@/pdf/templates/BillPDF';
import { generateQRCodeDataURL, generateCode128BarcodeDataURL } from '@/pdf/utils/barcodes';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

interface Step2TemplatesProps {
  templatesData: {
    contractFile: File | null;
    billFile: File | null;
    useDefaults: { contract: boolean; bill: boolean };
  };
  onChange: (field: string, value: File | null | { contract: boolean; bill: boolean }) => void;
}

export const Step2Templates: React.FC<Step2TemplatesProps> = ({ templatesData, onChange }) => {
  const { t } = useTranslation();
  const handleFileUpload = (type: 'contract' | 'bill', e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        alert(t('onboarding.step2.pdfTooLarge'));
        return;
      }
      if (file.type !== 'application/pdf') {
        alert(t('onboarding.step2.pdfMustBePdf'));
        return;
      }
      onChange(`${type}File`, file);
    }
  };

  const handlePreviewContract = async () => {
    try {
      toast.info(t('onboarding.step2.generatingContractPreview'));
      
      const sampleContractData: ContractData = {
        title: 'ENERGY SERVICE CONTRACT',
        contractName: 'Standard Prepaid Contract',
        pdl: '24567891234567',
        pricePerKwh: 'TZS 650.00',
        subscribedPower: '9 kVA',
        contractNumber: 'GE-CON-2024-SAMPLE',
        customerName: 'Sample Customer',
        date: new Date().toLocaleDateString('en-GB'),
      };

      const verifyUrl = `${window.location.origin}/contracts/verify?id=${sampleContractData.contractNumber}`;
      const qrDataUrl = await generateQRCodeDataURL(verifyUrl, 144);
      const barcodeDataUrl = generateCode128BarcodeDataURL(sampleContractData.pdl, { height: 60, width: 2 });

      const blob = await pdf(
        <ContractPDF data={sampleContractData} qrDataUrl={qrDataUrl} barcodeDataUrl={barcodeDataUrl} />
      ).toBlob();

      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(t('onboarding.step2.contractPreviewOpened'));
    } catch (error) {
      console.error('Error generating contract preview:', error);
      toast.error(t('onboarding.step2.contractPreviewFailed'));
    }
  };

  const handlePreviewBill = async () => {
    try {
      toast.info(t('onboarding.step2.generatingBillPreview'));
      
      const sampleBillData: BillData = {
        month: new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
        billNumber: 'GE-BILL-2024-SAMPLE',
        issueDate: new Date().toLocaleDateString('en-GB'),
        period: new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
        energyConsumed: '385 kWh',
        ratePerKwh: 'TZS 650.00',
        totalAmount: 'TZS 250,250.00',
        status: 'pending',
      };

      const verifyUrl = `${window.location.origin}/bills/verify?bill=${sampleBillData.billNumber}`;
      const qrDataUrl = await generateQRCodeDataURL(verifyUrl, 144);
      const barcodeDataUrl = generateCode128BarcodeDataURL(sampleBillData.billNumber, { height: 60, width: 2 });

      const blob = await pdf(
        <BillPDF data={sampleBillData} qrDataUrl={qrDataUrl} barcodeDataUrl={barcodeDataUrl} />
      ).toBlob();

      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(t('onboarding.step2.billPreviewOpened'));
    } catch (error) {
      console.error('Error generating bill preview:', error);
      toast.error(t('onboarding.step2.billPreviewFailed'));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <FileText className="h-8 w-8 text-primary" />
        <div>
          <h2 className="text-2xl font-bold text-foreground">{t('onboarding.step2.title')}</h2>
          <p className="text-muted-foreground">{t('onboarding.step2.subtitle')}</p>
        </div>
      </div>

      {/* Contract Template */}
      <div className="border rounded-lg p-6 space-y-4 bg-card">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-lg">{t('onboarding.step2.contractTemplate')}</h3>
            <p className="text-sm text-muted-foreground">{t('onboarding.step2.contractTemplateDesc')}</p>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="useDefaultContract" className="text-sm">{t('onboarding.step2.useDefault')}</Label>
            <Switch
              id="useDefaultContract"
              checked={templatesData.useDefaults.contract}
              onCheckedChange={(checked) => {
                onChange('useDefaults', { ...templatesData.useDefaults, contract: checked });
                if (checked) onChange('contractFile', null);
              }}
            />
          </div>
        </div>

        {!templatesData.useDefaults.contract && (
          <div>
            {templatesData.contractFile ? (
              <div className="flex items-center gap-3 p-4 border rounded-lg bg-muted/50">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                <div className="flex-1">
                  <p className="font-medium">{templatesData.contractFile.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {(templatesData.contractFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => onChange('contractFile', null)}
                  className="text-destructive hover:underline text-sm"
                >
                  {t('onboarding.step2.remove')}
                </button>
              </div>
            ) : (
              <label
                htmlFor="contractUpload"
                className="flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-8 cursor-pointer hover:bg-muted/50 transition-colors"
              >
                <Upload className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-sm font-medium">{t('onboarding.step2.uploadContractTemplate')}</p>
                <p className="text-xs text-muted-foreground mt-1">{t('onboarding.step2.pdfUpTo10MB')}</p>
              </label>
            )}
            <input
              id="contractUpload"
              type="file"
              accept="application/pdf"
              onChange={(e) => handleFileUpload('contract', e)}
              className="hidden"
            />
          </div>
        )}

        {templatesData.useDefaults.contract && (
          <div className="flex items-center gap-2 p-4 bg-primary/10 rounded-lg">
            <CheckCircle2 className="h-5 w-5 text-primary" />
            <p className="text-sm text-muted-foreground flex-1">{t('onboarding.step2.usingDefaultContract')}</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handlePreviewContract}
            >
              <Eye className="h-4 w-4 mr-2" />
              {t('onboarding.step2.preview')}
            </Button>
          </div>
        )}
      </div>

      {/* Bill Template */}
      <div className="border rounded-lg p-6 space-y-4 bg-card">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-lg">{t('onboarding.step2.billTemplate')}</h3>
            <p className="text-sm text-muted-foreground">{t('onboarding.step2.billTemplateDesc')}</p>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="useDefaultBill" className="text-sm">{t('onboarding.step2.useDefault')}</Label>
            <Switch
              id="useDefaultBill"
              checked={templatesData.useDefaults.bill}
              onCheckedChange={(checked) => {
                onChange('useDefaults', { ...templatesData.useDefaults, bill: checked });
                if (checked) onChange('billFile', null);
              }}
            />
          </div>
        </div>

        {!templatesData.useDefaults.bill && (
          <div>
            {templatesData.billFile ? (
              <div className="flex items-center gap-3 p-4 border rounded-lg bg-muted/50">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                <div className="flex-1">
                  <p className="font-medium">{templatesData.billFile.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {(templatesData.billFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => onChange('billFile', null)}
                  className="text-destructive hover:underline text-sm"
                >
                  {t('onboarding.step2.remove')}
                </button>
              </div>
            ) : (
              <label
                htmlFor="billUpload"
                className="flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-8 cursor-pointer hover:bg-muted/50 transition-colors"
              >
                <Upload className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-sm font-medium">{t('onboarding.step2.uploadBillTemplate')}</p>
                <p className="text-xs text-muted-foreground mt-1">{t('onboarding.step2.pdfUpTo10MB')}</p>
              </label>
            )}
            <input
              id="billUpload"
              type="file"
              accept="application/pdf"
              onChange={(e) => handleFileUpload('bill', e)}
              className="hidden"
            />
          </div>
        )}

        {templatesData.useDefaults.bill && (
          <div className="flex items-center gap-2 p-4 bg-primary/10 rounded-lg">
            <CheckCircle2 className="h-5 w-5 text-primary" />
            <p className="text-sm text-muted-foreground flex-1">{t('onboarding.step2.usingDefaultBill')}</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handlePreviewBill}
            >
              <Eye className="h-4 w-4 mr-2" />
              {t('onboarding.step2.preview')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
